import { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Navbar, Footer } from './components/layout';
import { HomePage } from './components/home';
import AsistenteChat from './components/common/AsistenteChat';
import AnalyticsTracker from './components/common/AnalyticsTracker';
import CampaignParamsKeeper from './components/common/CampaignParamsKeeper';
import { SeoProvider } from './components/common/Seo';
import analyticsService from './services/analyticsService';
import { AuthModal } from './components/auth';
import Products from './components/products/Products';
import ProductDetailPage from './pages/ProductDetailPage';
import { CartPage, CartModal } from './components/cart';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import PaymentResult from './components/payments/PaymentResult';
import MyPaymentsPage from './pages/MyPaymentsPage';
import MisPedidosPage from './pages/MisPedidosPage';
import ProfilePage from './pages/ProfilePage';
import AhiLesDejoPage from './pages/AhiLesDejoPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import SeguimientoPedido from './pages/SeguimientoPedido';

// El panel de administración es, con diferencia, la parte más pesada de la
// aplicación (~20 paneles y recharts) y la que menos gente ve. Cargarlo aparte
// deja fuera del bundle principal todo eso, que hasta ahora descargaba también
// quien solo venía a ver la ficha de un juego.
const AdminRoutes = lazy(() => import('./pages/AdminRoutes'));

function CargandoPanel() {
  return <div className="p-10 text-center text-df-muted">Cargando panel…</div>;
}

export default function App() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [showCartModal, setShowCartModal] = useState(false);

  useEffect(() => {
    analyticsService.initAutoTracking();
  }, []);

  const handleAuthModalOpen = (mode = 'login') => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const handleCartClick = () => {
    setShowCartModal(true);
  };

  const googleClientId = '245111813935-0evgj9gk3plv0uv35ik0kboaat39sdiu.apps.googleusercontent.com';

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
    <AuthProvider>
      <CartProvider>
        <Router>
          {/* La cabecera de cada página la sirve ya resuelta el backend (ver
              seo_render_controller.go). Esto la mantiene al día al navegar
              dentro de la SPA, donde no hay petición al servidor. */}
          <SeoProvider>
          <AnalyticsTracker />
          <CampaignParamsKeeper />
          <div className="flex flex-col min-h-screen bg-df-bg">
            <div className="fixed w-full z-50">
              <Navbar onAuthModalOpen={handleAuthModalOpen} onCartClick={handleCartClick} />
            </div>

            <div className="flex-1 navbar-bottom-space">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/ahilesdejo" element={<AhiLesDejoPage />} />
                <Route path="/productos" element={<Products />} />
                {/* Ficha de producto. Al ser una ruta dinámica, todo producto
                    tiene su página en cuanto existe: no hay que generar ni
                    desplegar nada al crearlo. */}
                <Route path="/producto/:id" element={<ProductDetailPage />} />
                <Route path="/carrito" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/payment-result" element={<PaymentResult />} />
                <Route path="/mis-pagos" element={<MyPaymentsPage />} />
                <Route path="/pedidos" element={<MisPedidosPage />} />
                <Route path="/perfil" element={<ProfilePage />} />
                <Route path="/politica-privacidad" element={<PrivacyPolicyPage />} />
                <Route path="/terminos-condiciones" element={<TermsConditionsPage />} />
                <Route path="/seguimiento" element={<SeguimientoPedido />} />
                
                {/* Panel de administración. Se carga aparte: ver pages/AdminRoutes.jsx */}
                <Route
                  path="/admin/*"
                  element={
                    <Suspense fallback={<CargandoPanel />}>
                      <AdminRoutes />
                    </Suspense>
                  }
                />
              </Routes>
            </div>

            <Footer />

            {/* Modal de autenticación */}
            <AuthModal
              isOpen={authModalOpen}
              onClose={handleAuthModalClose}
              initialMode={authModalMode}
            />

            {/* Modal de carrito */}
            <CartModal isOpen={showCartModal} onClose={() => setShowCartModal(false)} />
            <AsistenteChat />
          </div>
          </SeoProvider>
        </Router>
      </CartProvider>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}
